import {Trans} from '@lingui/macro';
import {noop} from 'lodash';
import React, {useMemo} from 'react';

import {useRuleEditorContext} from '../../../../editors/ruleEditor/ruleEditorContext';
import {RuleConditionTypesEnum} from '../../../ruleConditionModel';
import {RuleTriggersTypesEnum} from '../../../ruleTriggerModel';
import {RuleConditionCard} from '../../ruleConditionCard';
import {RuleEditorConditions} from '../../ruleEditorConditions';
import {StyledRuleEditorForm} from '../../ruleEditorForm';
import {RuleEditorSection} from '../../ruleEditorSection';
import {RuleTriggerCard} from '../../ruleTriggerCard';
import {RuleEditorRecipeStep} from '../components/ruleEditorRecipeStep';
import {useRuleConditionWithParams} from '../helpers/recipeHooksHelpers';
import {CsatEditorScheduledActions} from './csatEditorScheduledActions';
import {CsatEditorSentAction} from './csatEditorSentActions';
import {CsatEditorStateActions} from './hooks/useCsatEditorState';
import {CsatEditorState} from './state/csatEditorState';

/*
 * Props.
 */

interface CsatEditorFormProps {
  state: CsatEditorState;
  actions: CsatEditorStateActions;
}

/*
 * Component.
 */

export const CsatEditorForm: React.FC<CsatEditorFormProps> = (props) => {
  const {state, actions} = props;

  const {ruleIntegration} = useRuleEditorContext();

  const delaySinceTriggerParams = useMemo(
    () => state.csatState.delaySinceTrigger,
    [state.csatState.delaySinceTrigger],
  );
  const delaySinceTriggerCondition = useRuleConditionWithParams(
    RuleConditionTypesEnum.DELAY_SINCE_TRIGGER,
    delaySinceTriggerParams,
  );

  return (
    <StyledRuleEditorForm>
      <RuleEditorRecipeStep step={1}>
        <Trans id="csat-editor-step-1-title">Scope</Trans>
      </RuleEditorRecipeStep>
      <RuleEditorConditions
        description={<Trans id="csat-editor-step-1-conditions-title">Apply this CSAT when…</Trans>}
        conditions={state.ruleState.conditions}
        onAddCondition={actions.onAddCondition}
        onUpdateCondition={actions.onUpdateCondition}
        onDeleteCondition={actions.onDeleteCondition}
        onValidation={actions.onValidation}
      />
      <RuleEditorRecipeStep step={2}>
        <Trans id="csat-editor-step-2-title">Time delay</Trans>
      </RuleEditorRecipeStep>
      <RuleEditorSection description={<Trans id="csat-editor-step-2-trigger-title">Survey trigger…</Trans>}>
        <RuleTriggerCard
          onUpdateTrigger={noop}
          onValidation={noop}
          triggerWithConditions={{trigger: RuleTriggersTypesEnum.ARCHIVE, conditions: []}}
        />
      </RuleEditorSection>
      <RuleEditorSection
        description={<Trans id="csat-editor-step-2-delay-title">Survey is sent when…</Trans>}
      >
        <RuleConditionCard
          condition={delaySinceTriggerCondition}
          conditionDefinition={ruleIntegration.findConditionDefinition(delaySinceTriggerCondition)}
          onUpdateCondition={(condition) => {
            if (condition.type === RuleConditionTypesEnum.DELAY_SINCE_TRIGGER && condition.params.delay) {
              actions.onSetDelaySinceTrigger(condition.params);
            }
          }}
          onValidation={actions.onValidation}
        />
      </RuleEditorSection>
      <RuleEditorRecipeStep step={3}>
        <Trans id="csat-editor-step-3-title">Actions</Trans>
      </RuleEditorRecipeStep>
      <CsatEditorScheduledActions
        description={
          <Trans id="csat-editor-step-3-scheduled-actions-title">When survey is scheduled, do this…</Trans>
        }
        actions={actions}
        state={state}
      />
      <CsatEditorSentAction
        description={<Trans id="csat-editor-step-3-sent-actions-title">When survey is sent, do this…</Trans>}
        actions={actions}
        state={state}
      />
    </StyledRuleEditorForm>
  );
};
